import React, { memo, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { BASE as API_BASE } from '../../services/api';
import { toast } from '../../utils/toastBus';

function mediaUrl(u) {
  if (!u) return '';
  return String(u).startsWith('/uploads/') ? `${API_BASE}${u}` : String(u);
}

function toPageUrl(ad) {
  try {
    const origin = typeof window !== 'undefined' && window.location ? window.location.origin : '';
    const id = ad?._id || ad?.id;
    return (origin && id) ? `${origin}/ads/${encodeURIComponent(String(id))}` : '';
  } catch { return ''; }
}

function AdCard({
  ad,
  isOwner = false,
  onDeleted,
  onUpdated,
}) {
  const id = ad?._id || ad?.id;
  const [workingLike, setWorkingLike] = useState(false);
  const [workingDel, setWorkingDel] = useState(false);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(ad?.title || '');
  const [liked, setLiked] = useState(Boolean(ad?.liked));
  const [likes, setLikes] = useState(Number(ad?.likes ?? ad?.likeCount ?? 0));

  const audioRef = useRef(null);
  const audioUrl = useMemo(() => mediaUrl(ad?.audioUrl), [ad?.audioUrl]);
  const videoUrl = useMemo(() => mediaUrl(ad?.videoUrl), [ad?.videoUrl]);
  const imageUrl = useMemo(() => mediaUrl(ad?.posterUrl || ad?.imageUrl), [ad?.posterUrl, ad?.imageUrl]);
  const pageUrl = useMemo(() => toPageUrl(ad), [ad?._id]);

  return (
    <div style={{ background: '#111827', border: '1px solid #1f2937', borderRadius: 12, padding: 12 }}>
      <h4 style={{ color: '#e5e7eb', margin: '0 0 6px' }}>
        {editing ? (
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={{ width: '100%', padding: 6, borderRadius: 6, border: '1px solid #374151', background: '#0b0b0e', color: '#e5e7eb' }}
          />
        ) : (
          <Link to={pageUrl || '#'} style={{ color: '#e5e7eb', textDecoration: 'underline' }}>
            {ad?.title || 'Ad'}
          </Link>
        )}
      </h4>

      {videoUrl ? (
        <video src={videoUrl} controls preload="metadata" style={{ width: '100%', borderRadius: 8, marginBottom: 8 }} poster={imageUrl || undefined} />
      ) : imageUrl ? (
        <img src={imageUrl} alt="poster" loading="lazy" decoding="async" style={{ width: '100%', borderRadius: 8, marginBottom: 8, objectFit: 'cover', maxHeight: 220 }} />
      ) : null}

      {audioUrl && (
        <audio ref={audioRef} src={audioUrl} controls preload="metadata" style={{ width: '100%', marginBottom: 8 }} />
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          onClick={async () => {
            try {
              setWorkingLike(true);
              if (!id) return;
              if (liked) {
                await api.unlikeAd(id);
                setLiked(false);
                setLikes((n) => Math.max(0, n - 1));
              } else {
                await api.likeAd(id);
                setLiked(true);
                setLikes((n) => n + 1);
              }
            } catch {
              toast({ type: 'error', message: 'Like failed' });
            } finally {
              setWorkingLike(false);
            }
          }}
          disabled={workingLike}
          style={{ background: liked ? '#ef4444' : '#374151', color: '#e5e7eb', border: 0, borderRadius: 8, padding: '6px 10px', cursor: 'pointer' }}
        >{liked ? `♥ Liked (${likes})` : `♡ Like (${likes})`}</button>

        <button
          onClick={async () => {
            try {
              const shareTarget = pageUrl || audioUrl || imageUrl;
              if (!shareTarget) { toast({ type: 'error', message: 'Nothing to share' }); return; }
              if (navigator.share) {
                await navigator.share({ title: ad?.title || 'Ad', url: shareTarget });
              } else {
                await navigator.clipboard.writeText(shareTarget);
                toast({ type: 'success', message: 'Link copied' });
              }
            } catch {
              toast({ type: 'error', message: 'Share failed' });
            }
          }}
          style={{ background: '#374151', color: '#e5e7eb', border: 0, borderRadius: 8, padding: '6px 10px', cursor: 'pointer' }}
        >Share</button>

        {isOwner && !editing && (
          <button
            onClick={() => setEditing(true)}
            style={{ background: '#1f2937', color: '#e5e7eb', border: '1px solid #374151', borderRadius: 8, padding: '6px 10px', cursor: 'pointer' }}
          >Edit</button>
        )}
        {isOwner && editing && (
          <>
            <button
              onClick={async () => {
                try {
                  if (!id) return;
                  const res = await api.updateAd(id, { title: title.trim() });
                  if (res?.error) throw new Error(res.error);
                  toast({ type: 'success', message: 'Ad updated' });
                  setEditing(false);
                  onUpdated && onUpdated(res.item || { ...ad, title: title.trim() });
                } catch (e) {
                  toast({ type: 'error', message: e?.message || 'Update failed' });
                }
              }}
              style={{ background: '#10b981', color: '#0b0b0e', border: 0, borderRadius: 8, padding: '6px 10px', cursor: 'pointer' }}
            >Save</button>
            <button
              onClick={() => { setEditing(false); setTitle(ad?.title || ''); }}
              style={{ background: '#374151', color: '#e5e7eb', border: 0, borderRadius: 8, padding: '6px 10px', cursor: 'pointer' }}
            >Cancel</button>
          </>
        )}
        {isOwner && (
          <button
            onClick={async () => {
              try {
                if (!id) return;
                if (!window.confirm('Delete this ad?')) return;
                setWorkingDel(true);
                const res = await api.deleteAd(id);
                if (res?.error) throw new Error(res.error);
                toast({ type: 'success', message: 'Ad deleted' });
                onDeleted && onDeleted(id);
              } catch (e) {
                toast({ type: 'error', message: e?.message || 'Delete failed' });
              } finally {
                setWorkingDel(false);
              }
            }}
            disabled={workingDel}
            style={{ color: '#f87171', background: 'rgba(248, 113, 113, 0.1)', border: '1px solid rgba(248, 113, 113, 0.3)', borderRadius: 8, padding: '6px 10px', cursor: 'pointer' }}
          >Delete</button>
        )}
      </div>
    </div>
  );
}

export default memo(AdCard);
